import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { advisorChat } from '../api/client';

const SUGGESTED = [
  "Walk me through my bills for the rest of the month",
  "Is this a good week to move money to savings?",
  "Which credit card should I focus on paying off?",
  "Am I on track with my budget this period?",
  "What categories am I overspending in?",
  "How much should I keep in checking right now?",
];

const Advisor = ({ messages = [], setMessages = () => {} }) => {
  const { COLORS } = useTheme();
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const started = messages.length > 0;
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setInput('');

    const userMsg = { role: 'user', content };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const res   = await advisorChat(nextMessages);
      const reply = res.data?.reply || "Sorry, I couldn't process that.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Something went wrong. Try again in a moment." }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>

      {/* Header */}
      <div style={{ padding: '20px 16px 12px', borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.green})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
              🪙
            </div>
            <div>
              <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 16 }}>Penny</div>
              <div style={{ color: COLORS.green, fontSize: 11, fontWeight: 600 }}>● Your Financial Advisor</div>
            </div>
          </div>
          {started && (
            <button
              onClick={() => setMessages([])}
              style={{ padding: '6px 12px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: 12 }}
            >
              Clear chat
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12, scrollbarWidth: 'none' }}>

        {/* Welcome state */}
        {!started && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.accent}33, ${COLORS.green}33)`, border: `2px solid ${COLORS.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, marginBottom: 16 }}>
              🪙
            </div>
            <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Hi, I'm Penny!</div>
            <div style={{ color: COLORS.textMuted, fontSize: 13, textAlign: 'center', lineHeight: 1.6, marginBottom: 24, maxWidth: 280 }}>
              I know your balances, bills, income, and spending. Ask me anything about your finances.
            </div>
            <div style={{ width: '100%' }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10, textAlign: 'center' }}>Suggested questions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SUGGESTED.map((q, i) => (
                  <button key={i} onClick={() => send(q)}
                    style={{ padding: '10px 14px', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textAlign: 'left', lineHeight: 1.4 }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 8, alignItems: 'flex-end' }}>
            {msg.role === 'assistant' && (
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.green})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                🪙
              </div>
            )}
            <div style={{
              maxWidth: '78%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: msg.role === 'user' ? COLORS.accent : COLORS.surface,
              border: msg.role === 'user' ? 'none' : `1px solid ${COLORS.border}`,
              color: msg.role === 'user' ? '#fff' : COLORS.text,
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.green})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
              🪙
            </div>
            <div style={{ padding: '12px 16px', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '18px 18px 18px 4px', display: 'flex', gap: 4, alignItems: 'center' }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 7, height: 7, borderRadius: '50%', background: COLORS.textMuted,
                  animation: 'pulse 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  opacity: 0.5,
                }} />
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div style={{ padding: '12px 16px 16px', borderTop: `1px solid ${COLORS.border}`, background: COLORS.bg }}>
        {started && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {["Bills remaining", "Safe to save?", "Biggest overspend"].map((q, i) => (
              <button key={i} onClick={() => send(q)}
                style={{ padding: '4px 10px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 20, color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600 }}>
                {q}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask Penny anything..."
            rows={1}
            style={{
              flex: 1,
              padding: '12px 14px',
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              color: COLORS.text,
              fontFamily: 'inherit',
              fontSize: 14,
              resize: 'none',
              outline: 'none',
              lineHeight: 1.4,
              maxHeight: 100,
              overflowY: 'auto',
            }}
            onInput={e => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            style={{
              width: 44, height: 44, borderRadius: 12, border: 'none', flexShrink: 0,
              background: input.trim() && !loading ? COLORS.accent : COLORS.border,
              cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, transition: 'background 0.2s',
            }}
          >
            ↑
          </button>
        </div>
        <style>{`@keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:1} }`}</style>
      </div>
    </div>
  );
};

export default Advisor;
